/**
 * 화면 조건 판독기 — 지금 화면의 검색 조건을 viewStates 에서 읽어 챗 칩과 요청에 쓴다.
 *
 * 조건의 출처는 Redux viewStates 다.
 * 화면이 탭을 다시 열 때 복원하려고 마지막 검색 조건을 저장해 두는 곳이라, 화면 코드를 고치지 않고 읽을 수 있다.
 * viewStates 에는 조건보다 탭·버킷·레벨 옵션·페이지·레이아웃·데이터 캐시 같은 비조건 값이 훨씬 많다.
 * 그래서 값 모양으로 가려내지 않고 CONDITION_AXES 에 적은 키만 읽는다.
 * 새 조건 키를 받으려면 이 표에 행을 더한다.
 *
 * 규약
 *  - 칩과 요청은 같은 함수(buildScreenConditions)를 거친다. 보이는 것과 보내는 것이 같다.
 *  - 구독은 pickConditionRaw 의 원시 문자열 배열을 shallowEqual 로 비교한다.
 *    렌더 중에 {viewName} 만 다시 저장하는 화면이 있어 항목 객체는 매번 바뀌지만, 값 문자열은 그대로다.
 *  - 결과는 문자열만 담는다. 식별자(BigInt·긴 정수)와 boolean 은 조건으로 싣지 않는다.
 *  - 값 표기는 이름이 있으면 "이름(코드)" 다. 목록은 ", " 로 잇고, 값 경계에서 끊고 남은 수를 "+N" 으로 적는다.
 *    백엔드 자유 조회가 이 표기를 값 단위로 나눠 SQL 에 걸렸는지 판정한다(nl2sql_screen_scope).
 *  - 이름이 곧 대상의 키인 축(분류 시나리오)은 이름 하나를 그대로 싣는다(single).
 *    백엔드가 그 이름을 통째로 비교하므로 괄호 코드를 붙이거나 쉼표를 붙여 쓰지 않는다.
 *
 * 이 폴더(components/agent)는 pages 를 import 하지 않는다.
 * 예외로 pages/utils/code-util 하나만 쓴다 — 품목 타입 이름표(코드표)이지 화면 지식이 아니다.
 */

import { format } from "date-fns";

import CodeUtil from "@plannel/pages/utils/code-util";
import axiosUtils from "@plannel/utils/axios-bigint";

// 목록 값은 20개, 조건 하나는 300자까지 싣는다. 백엔드 screen_context 의 상한과 같다.
const MAX_VALUES = 20;
const MAX_TEXT = 300;

// 이 이상인 정수는 식별자로 본다(스노플레이크 id 는 19자리).
const ID_MIN = 1e11;

// 이름 뒤에 붙일 코드를 찾는 키 순서. 표에 없는 *Cd 키는 뒤에서 본다.
const CODE_KEYS = ["locCd", "hrchyCd", "itemCd", "customerCd", "custCd", "resCd", "wcCd",
  "routeCd", "supplierCd", "orderCd", "cd", "code"];
// 상태·유형·상위·단위 같은 속성 코드는 대상의 코드가 아니다. 키 어디에 있어도 거른다(itemTypeCd·bucketCd).
const NOT_CODE_RE = /(status|type|module|parent|grp|group|bucket|unit|uom|currency|level)/i;

const isId = (x) => typeof x === "bigint"
  || (typeof x === "number" && Number.isInteger(x) && Math.abs(x) >= ID_MIN);

const scalarText = (x) => {
  if (typeof x === "string") return x.split(/\s+/).filter(Boolean).join(" ") || null;
  if (typeof x === "number" && Number.isFinite(x) && !isId(x)) return String(x);
  return null;
};

const codeOf = (obj) => {
  for (const k of CODE_KEYS) {
    const s = isId(obj[k]) ? null : scalarText(obj[k]);
    if (s) return s;
  }
  for (const k of Object.keys(obj)) {
    if (!k.endsWith("Cd") || NOT_CODE_RE.test(k) || isId(obj[k])) continue;
    const s = scalarText(obj[k]);
    if (s) return s;
  }
  return null;
};

const nameOf = (obj) => {
  const s = scalarText(obj.name);
  if (s) return s;
  const k = Object.keys(obj).find((key) => key.endsWith("Nm") && scalarText(obj[key]));
  return k ? scalarText(obj[k]) : null;
};

// 문자열·숫자·이름 있는 객체 하나를 표기로 바꾼다. 트리의 전체 노드(topFlg)는 조건이 아니다.
const namedText = (x) => {
  if (x == null || typeof x === "boolean") return null;
  if (typeof x !== "object") return scalarText(x);
  if (Array.isArray(x) || x.topFlg) return null;
  const name = nameOf(x);
  const code = codeOf(x);
  if (name && code && name !== code) return `${name}(${code})`;
  return name || code;
};

const asList = (x) => (Array.isArray(x) ? x : x == null ? [] : [x]);
const namedList = (x) => asList(x).map(namedText).filter(Boolean);

// 이름 하나 — 행에 붙은 다른 *Cd(분류 단위 classifiDimCd 등)를 코드로 붙이지 않는다.
const nameOnly = (x) => {
  if (x == null || typeof x === "boolean" || Array.isArray(x)) return [];
  const s = typeof x === "object" ? nameOf(x) : scalarText(x);
  return s ? [s] : [];
};
const firstNonEmpty = (...lists) => lists.find((l) => l.length) ?? [];
const unique = (list) => [...new Set(list)];

// 출발·도착 두 칸짜리 거점 검색(BOD 계열·출하 거래)인지.
const isLocPair = (x) => x != null && typeof x === "object" && !Array.isArray(x)
  && ("originLocation" in x || "destLocation" in x);

// 날짜 한 칸 → 로컬 yyyy-MM-dd. 저장된 ISO 는 UTC 라 그대로 자르면 하루 밀린다.
//   ISO 모양만 날짜로 본다. Date 파서는 "5" 같은 문자열도 날짜로 읽는다.
const dateText = (x) => {
  if (x == null || x === "") return null;
  if (typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x)) return x;
  const iso = typeof x === "string" && /^\d{4}-\d{2}-\d{2}T/.test(x);
  const d = x instanceof Date ? x : iso ? new Date(x) : null;
  return d && !Number.isNaN(d.getTime()) ? format(d, "yyyy-MM-dd") : null;
};

const periodText = (start, end) => {
  const a = dateText(start);
  const b = dateText(end);
  return a || b ? [`${a ?? ""} ~ ${b ?? ""}`.trim()] : [];
};

// 품목 타입은 "이름(코드)" 로 적는다 — 자유 조회가 코드 컬럼(item_type)에 걸 수 있어야 한다.
const itemTypeText = (x, t) => asList(x).map((it) => {
  const cd = typeof it === "object" && it ? scalarText(it.cd) : scalarText(it);
  const key = cd ? CodeUtil.itemType?.[cd]?.name : null;
  return key ? `${t(key)}(${cd})` : namedText(it);
}).filter(Boolean);

// 번역 키로 저장된 값(기간 기준·제외 조건).
//   keepCode 가 false 면 번역이 없는 문자열을 버린다 — 기간 기준의 문자열 값은 화면 모드값이다.
const translatedText = (x, t, keepCode) => asList(x).map((it) => {
  if (it && typeof it === "object") {
    const key = scalarText(it.name);
    return key ? t(key) : namedText(it);
  }
  const s = scalarText(it);
  if (!s) return null;
  return t(s) !== s ? t(s) : keepCode ? s : null;
}).filter(Boolean);

/*
 * 허용 목록. 표의 순서가 조건이 나열되는 순서다. 한 축 안에서 여러 키를 읽으면 먼저 값이 나온 키를 쓴다.
 * 축 이름 목록은 백엔드 screen_context.SCREEN_CONDITION_KEYS 와 같아야 한다. 백엔드는 목록 밖의 축을 버린다.
 *   key      — 축 이름(백엔드로 가는 내부 표기, 답변에는 쓰지 않는다)
 *   labelKey — 화면 라벨 i18n 키(6개 언어 menu 섹션에 있다)
 *   raw      — 읽는 viewState 최상위 키
 *   read     — 파싱한 값 → 표기 목록
 *   dropVersionNames — 버전 칩과 같은 대상을 가리킬 수 있는 축. 버전 칩에 보이는 이름은 뺀다.
 *   single   — 값 하나를 목록 표기 없이 그대로 싣는 축. 값 안의 ", " 를 붙이지 않는다.
 */
const CONDITION_AXES = [
  { key: "period", labelKey: "period", raw: ["period", "start", "end", "startDate", "endDate"],
    read: (v) => {
      const p = v.period && typeof v.period === "object" && !Array.isArray(v.period) ? v.period : null;
      if (p) return periodText(p.start, p.end);
      if (v.start || v.end) return periodText(v.start, v.end);
      return periodText(v.startDate, v.endDate);
    } },
  { key: "period_basis", labelKey: "periodSearchCondition", raw: ["selectedSearch"],
    read: (v, t) => translatedText(v.selectedSearch, t, false) },
  { key: "location", labelKey: "location", raw: ["filter", "location", "locationSearch"],
    read: (v) => firstNonEmpty(namedList(v.filter?.locations), namedList(v.location),
      isLocPair(v.locationSearch) ? [] : namedList(v.locationSearch)) },
  { key: "origin_location", labelKey: "originLocation", raw: ["filter", "locationSearch"],
    read: (v) => firstNonEmpty(namedList(v.filter?.originLocation),
      isLocPair(v.locationSearch) ? namedList(v.locationSearch.originLocation) : []) },
  { key: "dest_location", labelKey: "destLocation", raw: ["filter", "locationSearch"],
    read: (v) => firstNonEmpty(namedList(v.filter?.destLocation),
      isLocPair(v.locationSearch) ? namedList(v.locationSearch.destLocation) : []) },
  { key: "vendor_location", labelKey: "vendorLocation", raw: ["filter"],
    read: (v) => namedList(v.filter?.vendorLocation) },
  { key: "inhouse_location", labelKey: "locationInhouse", raw: ["locationInhouse"],
    read: (v) => namedList(v.locationInhouse) },
  { key: "supplier_location", labelKey: "locationSupplier", raw: ["locationSupplier"],
    read: (v) => namedList(v.locationSupplier) },
  { key: "item_hierarchy", labelKey: "itemHrchy", raw: ["filter"],
    read: (v) => namedList(v.filter?.category) },
  { key: "customer_hierarchy", labelKey: "customerHrchy", raw: ["filter"],
    read: (v) => namedList(v.filter?.customerHrchy ?? v.filter?.customerHrchys) },
  { key: "item", labelKey: "item", raw: ["itemSearch", "extraItemCodes"],
    read: (v) => unique([...namedList(v.itemSearch), ...namedList(v.extraItemCodes)]) },
  { key: "customer", labelKey: "customer", raw: ["customerSearch", "extraCustomerCodes", "npiCustomer"],
    read: (v) => firstNonEmpty(
      unique([...namedList(v.customerSearch), ...namedList(v.extraCustomerCodes)]),
      namedList(v.npiCustomer)) },
  { key: "npi", labelKey: "npi", raw: ["selectedNpis"], read: (v) => namedList(v.selectedNpis) },
  // resourceSearch 는 검색 칸의 선택값, resource 는 자원 필터(ResourceFilter)의 선택값이다.
  { key: "resource", labelKey: "resource", raw: ["resourceSearch", "resource"],
    read: (v) => firstNonEmpty(namedList(v.resourceSearch), namedList(v.resource)) },
  { key: "workcenter", labelKey: "workcenter", raw: ["workcenterSearch"],
    read: (v) => namedList(v.workcenterSearch) },
  { key: "route", labelKey: "route", raw: ["routeSearch"], read: (v) => namedList(v.routeSearch) },
  { key: "supplier", labelKey: "supplier", raw: ["supplierSearch"], read: (v) => namedList(v.supplierSearch) },
  { key: "order", labelKey: "orderCd", raw: ["orderCd"], read: (v) => namedList(v.orderCd) },
  // ABC-XYZ 분류 시나리오 — 이름이 UNIQUE 키다. 기본 이름에 쉼표·괄호가 흔하다("7:2:1 on 6M, Item (Profit)").
  { key: "classification", labelKey: "classification", raw: ["classification"], single: true,
    read: (v) => nameOnly(v.classification) },
  { key: "abc_xyz_class", labelKey: "invClassCd", raw: ["invClassCdList"],
    read: (v) => namedList(v.invClassCdList) },
  { key: "item_type", labelKey: "itemType", raw: ["itemType", "itemTypeSearch"],
    read: (v, t) => itemTypeText(v.itemType ?? v.itemTypeSearch, t) },
  { key: "excluded", labelKey: "excludeCondition", raw: ["appliedExceptions"],
    read: (v, t) => translatedText(v.appliedExceptions, t, true) },
  { key: "selected_scenario", labelKey: "scenario", raw: ["scenario"], read: (v) => namedList(v.scenario),
    dropVersionNames: true },
];

const RAW_KEYS = unique(CONDITION_AXES.flatMap((a) => a.raw));
const NO_RAW = Object.freeze([]);

/**
 * 구독용 원시값 — 허용 목록이 읽는 최상위 키의 저장 문자열을 RAW_KEYS 순서로 돌려준다.
 * 항목이 없으면 같은 빈 배열을 돌려준다(shallowEqual 이 매번 같다고 본다).
 */
export const pickConditionRaw = (viewInfos, viewName) => {
  if (!viewName || !Array.isArray(viewInfos)) return NO_RAW;
  const entry = viewInfos.find((v) => v?.viewName === viewName);
  return entry ? RAW_KEYS.map((k) => entry[k]) : NO_RAW;
};

const parseRaw = (x) => {
  if (typeof x !== "string") return x;
  try {
    return axiosUtils.parseJsonWithBigInt(x);
  } catch {
    return x;
  }
};

// 목록은 ", " 로 잇는다. 값 안의 ", " 는 "," 로 붙인다 — 값 경계와 겹치면 백엔드가 값을 잘못 나눈다.
const joinValues = (rawValues) => {
  const values = rawValues.map((s) => s.split(", ").join(","));
  const kept = [];
  let len = 0;
  for (const s of values) {
    const add = (kept.length ? 2 : 0) + s.length;
    // 뒤에 붙을 " +N" 자리를 남겨 둔다.
    if (kept.length >= MAX_VALUES || len + add > MAX_TEXT - 6) break;
    kept.push(s);
    len += add;
  }
  if (!kept.length) return `${values[0].slice(0, MAX_TEXT - 7)}…${values.length > 1 ? ` +${values.length - 1}` : ""}`;
  const rest = values.length - kept.length;
  return kept.join(", ") + (rest ? ` +${rest}` : "");
};

/**
 * 원시값 → 화면 조건 [{key, label, text}].
 * @param {Array} raw           pickConditionRaw 의 결과
 * @param {object} opts.t       i18n t
 * @param {string[]} opts.versionNames 버전 칩에 이미 보이는 버전 이름(dropVersionNames 축에서 뺀다)
 */
export const buildScreenConditions = (raw, { t, versionNames = [] } = {}) => {
  if (!raw?.length) return [];
  const tr = typeof t === "function" ? t : (s) => s;
  const v = {};
  RAW_KEYS.forEach((k, i) => { v[k] = parseRaw(raw[i]); });
  const shownVersions = new Set(versionNames.filter(Boolean));
  const out = [];
  for (const axis of CONDITION_AXES) {
    let values = [];
    try {
      values = axis.read(v, tr);
    } catch {
      values = [];
    }
    if (axis.dropVersionNames) values = values.filter((s) => !shownVersions.has(s));
    if (!values.length) continue;
    const text = axis.single ? values[0].slice(0, MAX_TEXT) : joinValues(values);
    out.push({ key: axis.key, label: tr(axis.labelKey), text });
  }
  return out;
};

/** 칩 표시 — 칩 라벨은 앞 두 값, 툴팁은 줄마다 "라벨: 값". */
export const describeScreenConditions = (conds) => ({
  chipLabel: conds.slice(0, 2).map((c) => c.text).join(" · "),
  tooltipLines: conds.map((c) => `${c.label}: ${c.text}`),
});

/** 챗 요청에 실을 필드. 조건이 없으면 빈 객체(키 자체를 만들지 않는다). */
export const screenConditionsPayload = (conds) => (conds.length ? { screen_conditions: conds } : {});
